import { useTheme } from '@mui/material';
import { objectMap } from '@shared/util/object';
import { useEffect } from 'react';
import { CommonTheme } from './createMuiTheme';

const varify = (name: string) => `--${name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`;

const combineSources = (sources: Record<string, string>) => {
  const parts = Object.values(sources).join(' + ');
  if (!parts) {
    return '';
  }
  return `calc(${parts})`;
};

export const createVars = <TKey extends string>(vars: Record<TKey, string>) => ({
  ...objectMap<TKey, string, TKey, string>(
    vars,
    ([name, defaultValue]) => [name, `var(${varify(name)}, ${defaultValue})`] as const,
  ),
  defaults: vars,
});

type Params<T extends string> = {
  name: T;
  value: number | null | undefined;
  enabled?: boolean;
  // used for avoiding conflicts between different components
  context?: string;
};

export type SetCssVarHook<T extends string> = (params: Params<T>) => void;

const sources: Record<string, Record<string, string>> = {};

export const useBaseSetCssVar = <T extends string>({
  name,
  value,
  enabled = true,
  context = 'default',
}: Params<T>) => {
  const { sizing, cssVars } = useTheme<CommonTheme>();
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const defaultValue = cssVars.defaults[name as keyof typeof cssVars.defaults];

    if (value) {
      sources[name] = { ...sources[name], [context]: sizing(value) };
    } else if (sources[name]) {
      delete sources[name][context];
    }

    document.documentElement.style.setProperty(
      varify(name),
      combineSources(sources[name] || {}) || defaultValue!,
    );
    return () => {
      if (sources[name]) {
        delete sources[name][context];
      }
      document.documentElement.style.setProperty(
        varify(name),
        combineSources(sources[name] || {}) || defaultValue!,
      );
    };
  }, [context, cssVars, enabled, name, sizing, value]);
};
